import type { WeaponDef } from '../../sim/defs';
import { Stat } from '../../sim/stats';

/** A02 — FAISCEAU CONTINU. « Flux permanent, perforant, portée courte »
 *  (nœud `arme-faisceau`, 2 200 crédits, ouvert par « boss sans bombe »).
 *
 *  12,5 volées/s × 2 projectiles × 8 dégâts = 200 DPS. Le même nombre que les
 *  deux autres armes, au chiffre près.
 *
 *  ── Il n'y a pas de laser dans le moteur, et on n'en ajoute pas ───────────
 *  Le tir primaire émet des projectiles ponctuels vers le haut, un point c'est
 *  tout (`playerSystem`). Un vrai faisceau continu serait une nouvelle FORME
 *  d'émission, donc une modification de moteur — la frontière que tout le
 *  projet défend. On obtient le flux autrement, par la géométrie, exactement
 *  comme la Lance perforante obtient sa perforation :
 *
 *    espacement de colonne = vitesse ÷ cadence
 *      PLASMA   1 800 ÷ 4,0  = 450,0 u
 *      VULCAN   1 800 ÷ 10,0 = 180,0 u
 *      FAISCEAU   990 ÷ 12,5 =  79,2 u
 *
 *  À 79 u, la colonne du FAISCEAU est 2,3 fois plus dense que celle du VULCAN.
 *  Dans une file d'ennemis haute de 240 u, elle présente trois projectiles là
 *  où le VULCAN en présente un : le premier meurt sur sa cible, les suivants
 *  poursuivent. La perforation est ÉMERGENTE — zéro champ dans le pool, zéro
 *  branche dans la boucle de collision, qui est la plus chaude du jeu.
 *
 *  ── ⚠️ La densité vient de la LENTEUR, pas de la cadence ──────────────────
 *  C'est le point contre-intuitif de cette arme, et il a été mesuré et non
 *  deviné. L'écriture naturelle d'un « flux permanent » est une grosse
 *  multiplication de cadence — ×2, ×2,5 — compensée par des dégâts réduits
 *  d'autant. Elle est INJOUABLE ici, à cause du plafond dur
 *  `STAT_CAPS.fireRate = 45`, qui écrête en silence tout ce qui le dépasse.
 *
 *  Mesure sur 400 builds simulés jusqu'au niveau 20 (voir
 *  `tests/game/vaisseaux-armes.test.ts`), multiplicateur de cadence atteint par
 *  les cartes seules : médiane ×2,5, p90 ×3,9, maximum observé ×10,7. Le DPS
 *  d'un profil de cadence, relativement au VULCAN sur le MÊME build :
 *
 *    profil ×2,50 → médiane 0,72 · 5ᵉ centile 0,40 · 85 % des builds pénalisés
 *    profil ×1,60 → médiane 1,00 · 5ᵉ centile 0,63 · 33 % des builds pénalisés
 *    profil ×1,25 → médiane 1,00 · 5ᵉ centile 0,80 · 13 % des builds pénalisés
 *
 *  Autrement dit : l'arme « qui sent le flux » aurait perdu 28 % de son DPS
 *  dans le build MÉDIAN, et jusqu'à 60 % chez le joueur qui construit vers la
 *  cadence — c'est-à-dire précisément celui qui l'achète. Un piège tendu à sa
 *  propre clientèle, sans un mot d'avertissement nulle part.
 *
 *  La cadence est donc limitée à ×1,25 et la densité obtenue en RALENTISSANT
 *  les projectiles : `bulletSpeed` n'a pas de plafond, l'espacement de colonne
 *  ne dépend que du rapport, et le résultat est le même à l'écran. On paie la
 *  forme sur la statistique qui peut la payer.
 *
 *  ── « Portée courte » se paie en TEMPS DE VOL ─────────────────────────────
 *  Il n'existe pas de portée dans le moteur : un projectile vit jusqu'au bord
 *  du terrain. Le seul levier honnête est la vitesse — 990 u/s contre 1 800,
 *  soit 1,32 s pour traverser l'écran au lieu de 0,73 s. Un ennemi qui apparaît
 *  en haut a une demi-seconde de plus pour agir avant d'être touché, et une
 *  cible qui se déplace latéralement a le temps de sortir de la colonne. C'est
 *  une portée utile plus courte, pas une portée courte — et il vaut mieux
 *  l'écrire que de laisser croire à un rayon d'action.
 *
 *  ⚠️ 990 u/s est proche du plancher de lisibilité : les projectiles du joueur
 *  doivent rester nettement plus rapides que ceux des ennemis (450 u/s), faute
 *  de quoi la hiérarchie de lecture de l'écran s'effondre. C'est ce plancher, et
 *  non le goût, qui interdit de descendre plus bas pour densifier davantage. */
const faisceau: WeaponDef = {
  id: 'faisceau',
  name: 'Faisceau continu',
  modifiers: [
    // Produit exactement 1 : 1,25 × 0,8. Voir `plasma.def.ts` pour le pourquoi
    // du tout-multiplicatif — il conserve le rapport de DPS avec le VULCAN quoi
    // que la run ajoute ensuite, additifs compris.
    { stat: Stat.FireRate, op: 'mul', value: 0.25 },
    { stat: Stat.Damage, op: 'mul', value: -0.2 },
    { stat: Stat.BulletSpeed, op: 'mul', value: -0.45 },
  ],
  text: '12,5 volées/s × 2 × 8 dégâts — 200 DPS, colonne dense à 990 u/s',
};

export default faisceau;
